import type { ListStackableCasesResponse } from '@compass/client/types/call';
import { callService } from '@services/api';
import { useQuery } from '@tanstack/react-query';

/** Org and program of the case a call was placed from, captured at dial time. */
export interface StackScope {
  organizationId: string;
  programId: string;
}

/**
 * How often the stackable list refreshes during a live call. GlobalDialpad polls
 * it for the whole call, so the Stack call tab's count stays current on DTMF too.
 */
export const STACKABLE_POLL_MS = 5000;

export const stackableCasesKey = (caseRecordId: string, callId: string) =>
  ['calls', 'stackable', caseRecordId, callId] as const;

/**
 * Query options for the cases the agent can stack onto a live call. Polls every
 * 5s only while the tab is visible (React Query pauses it in the background),
 * refetches on focus, and is never served stale, so opening the panel fetches
 * at once. Exported so the policy is testable without timers.
 */
export const stackableCasesQueryOptions = (
  scope: StackScope | null,
  caseRecordId: string,
  callId?: string,
) => ({
  queryKey: stackableCasesKey(caseRecordId, callId ?? ''),
  enabled: Boolean(scope && caseRecordId && callId),
  queryFn: () =>
    callService.listStackableCases({
      organization_id: scope!.organizationId,
      program_id: scope!.programId,
      case_record_id: caseRecordId,
      call_id: callId!,
    }),
  refetchInterval: STACKABLE_POLL_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  staleTime: 0,
  // A failed poll keeps the last list on screen; the next tick retries.
  retry: false,
});

export const useStackableCases = (
  scope: StackScope | null,
  caseRecordId: string,
  callId?: string,
) => useQuery<ListStackableCasesResponse>(stackableCasesQueryOptions(scope, caseRecordId, callId));

/**
 * Query options for the post-call summary: the same list, read once. The stack's
 * members come back after hangup too (in_this_stack), and this route is allowed
 * at assigned scope on the parent case, so a Case Manager can read their own
 * call's summary. Its own key, so it never shares the in-call poll's entry.
 */
export const stackedCasesSummaryQueryOptions = (
  scope: StackScope | null,
  caseRecordId: string,
  callId?: string,
) => ({
  ...stackableCasesQueryOptions(scope, caseRecordId, callId),
  queryKey: [...stackableCasesKey(caseRecordId, callId ?? ''), 'summary'] as const,
  refetchInterval: false as const,
  refetchOnWindowFocus: false,
  staleTime: 0,
  retry: false,
});

export const useStackedCasesSummary = (
  scope: StackScope | null,
  caseRecordId: string,
  callId?: string,
) =>
  useQuery<ListStackableCasesResponse>(
    stackedCasesSummaryQueryOptions(scope, caseRecordId, callId),
  );
